/**
 * **Perakitan dokumen penjualan di TABLET** (K13 tier satu-tablet, Fase 3).
 *
 * ### ⭐⭐⭐ Menghitung dengan kode yang SAMA PERSIS dengan server
 * Totalnya dihitung `@osbpos/domain` — paket yang dipakai portal untuk menghitung bill. Inilah
 * alasan `packages/domain` dipisah sejak awal: ia satu-satunya cara klien menghitung dengan kode
 * yang sama, bukan dengan tiruan yang akan menyimpang diam-diam.
 *
 * ⛔ Kalau hitungan di sini meleset satu rupiah pun dari server, **setiap penjualan tablet ditolak**
 * (`SYNC-409-VARIANCE`). Itu bukan kelemahan rancangan — itu gerbangnya bekerja: lebih baik
 * penjualan tertahan dan terlihat daripada angka yang salah masuk buku diam-diam.
 *
 * ### ⭐ Kenapa di `packages/domain`, bukan di `pos-web`
 * Hitungan penjualan harus bisa dipakai klien mana pun — termasuk yang belum ada — dan menaruhnya
 * di aplikasi berarti klien berikutnya menyalinnya. Salinan hitungan uang adalah salinan yang
 * menyimpang, dan yang menyimpang di sini menghasilkan selisih pada transaksi pertama.
 *
 * ### ⚠️ Aturan pajak datang dari KATALOG, tak pernah dari layar
 * Katalog sudah menyaringnya ke outlet + tanggal + kanal. Menyalin aturan keberlakuan ke sini
 * berarti dua salinan yang menyimpang — dan yang menyimpang di klien menghasilkan selisih pada
 * transaksi pertama, di outlet yang paling tak sanggup mendiagnosanya.
 */
import { addMoney, money, moneyToWire, mulMoneyByQty, qty, uuidv7 } from '@osbpos/contract';
import { promoDiscount, promoTargetsProduct } from './promo-engine.js';
import { computeOrderTax } from './tax-engine.js';
/**
 * Menghitung total & menyusun dokumen penjualan.
 *
 * ⛔ Melempar bila katalognya belum membawa aturan pajak. Menghitung "seadanya tanpa pajak" jauh
 * lebih berbahaya daripada menolak: angkanya terlihat wajar, tamunya membayar kurang, dan yang
 * menemukannya baru tutup buku.
 */
export function assembleSale(input) {
    const tax = input.catalog.tax;
    if (tax === undefined) {
        throw new Error('katalog belum membawa aturan pajak — tak boleh menghitung sendiri');
    }
    const lines = input.lines.map((l) => ({
        lineId: uuidv7(),
        code: l.code,
        /* ⭐ gross = harga satuan × jumlah, dihitung dengan aritmetika desimal eksak (P1). */
        /*
         * ⛔ Mode pembulatan `half_up` disebut EKSPLISIT, dan wajib sama dengan yang dipakai server
         * (`OrderService.addLine`). Pembulatan yang berbeda di dua sisi menghasilkan selisih satu
         * rupiah pada baris ganjil — cukup untuk membuat SETIAP penjualan tablet ditolak.
         */
        /* ⭐ Harga efektif = dasar + Σ tambahan modifier. Lihat `modifierDelta`. */
        grossAmount: mulMoneyByQty(addMoney(money(l.price), money(l.modifierDelta ?? '0')), qty(String(l.qty)), 'half_up'),
        /* ⚠️ Potongan PER BARIS belum ada di tier ini — dinyatakan nol, bukan dihilangkan (P3).
           Promo di tier ini selalu mendarat sebagai potongan tingkat PESANAN (`orderDiscount`). */
        discountAmount: money('0'),
        /*
         * ⭐⭐ Kode pajak per PRODUK (K14). Produk yang tak ada di peta = `[]` = **pernyataan** "tak
         * kena apa pun", bukan "belum disetel" — keduanya memang berarti sama di sini.
         */
        applicableTaxCodes: tax.perProduct[l.code] ?? [],
    }));
    /*
     * ⭐⭐⭐ **Potongan promo dihitung DI SINI, dengan mesin bersama** (2026-08-25).
     *
     * ⛔ Dasarnya hanya baris yang LAYAK — cermin persis `PromoService.apply` di server:
     * (a) produknya boleh didiskon, dan (b) ia disasar promo ini (tanpa sasaran = semua).
     * Menghitung dari seluruh bill berarti potongan bocor ke rokok & voucher, dan selisihnya
     * menolak seluruh dokumen (`SYNC-409-VARIANCE`) — bukan "diskon yang agak besar".
     *
     * ⚠️ Yang TAK diperiksa di sini: masa berlaku, belanja minimum, dan persetujuan penyelia. Itu
     * keputusan yang butuh KALIMAT untuk kasir, dan fungsi murni tak punya tempat menaruhnya.
     */
    const orderDiscount = (() => {
        if (input.promo === undefined)
            return money('0');
        const layak = lines.filter((l, i) => {
            const asal = input.lines[i];
            if (asal?.discountAllowed === false)
                return false;
            return promoTargetsProduct(input.promo, l.code);
        });
        if (layak.length === 0)
            return money('0');
        const dasar = layak.reduce((acc, l) => addMoney(acc, l.grossAmount), money('0'));
        return promoDiscount(input.promo, dasar);
    })();
    const result = computeOrderTax({
        lines: lines.map((l) => ({
            lineId: l.lineId,
            grossAmount: l.grossAmount,
            discountAmount: l.discountAmount,
            applicableTaxCodes: l.applicableTaxCodes,
        })),
        orderDiscount,
        taxCodes: tax.codes,
    });
    const grandTotal = moneyToWire(result.grandTotal);
    const orderId = uuidv7();
    return {
        doc: {
            orderId,
            /*
             * ⭐⭐ Catatan lama di sini berbunyi: *"nomor berurut menuntut wasit yang tak ada di tier ini,
             * dan dua tablet yang sama-sama menebak 'berikutnya' menghasilkan nomor kembar"*. Itu benar
             * untuk urutan **bersama** — dan justru itu yang tak dipakai lagi: urutnya **per perangkat**,
             * berprefiks `device_code` yang sudah unik per outlet (`device_code_uniq`). Dua tablet tak
             * bisa kembar karena prefiksnya berbeda, dan tak seorang pun perlu jadi wasit.
             */
            orderNo: input.orderNo,
            ...(input.promo === undefined ? {} : { promoCode: input.promo.code }),
            outletCode: input.outletCode,
            deviceCode: input.deviceCode,
            ...(input.cashierCode === undefined ? {} : { cashierCode: input.cashierCode }),
            ...(input.cashierName === undefined ? {} : { cashierName: input.cashierName }),
            businessDate: input.businessDate,
            orderType: input.orderType,
            entryChannel: 'pos',
            priceChannel: input.catalog.priceChannel,
            /* ⭐ Penanda & jumlah tamu dari layar kasir (K31) — tanpa ini tiket dapurnya tak bernama. */
            guestCount: input.guestCount ?? 1,
            ...(input.parkKind === undefined ? {} : { parkKind: input.parkKind }),
            ...(input.parkLabel === undefined ? {} : { parkLabel: input.parkLabel }),
            /* ⚠️ Dihilangkan bila tak ada, bukan dikirim `null`: server membedakan "tanpa sesi" dari
               "sesi yang tak dikenal", dan `null` akan terbaca sebagai yang kedua. */
            ...(input.shiftId === undefined ? {} : { shiftId: input.shiftId }),
            lines: lines.map((l) => ({ lineId: l.lineId, code: l.code, productCode: l.code,
                qty: `${String(input.lines.find((x) => x.code === l.code)?.qty ?? 1)}.000000`,
                /* ⭐⭐ Harga yang benar-benar ditagihkan ikut naik (2026-08-25) — itu yang membuat portal
                   bisa MENERIMA dokumennya utuh alih-alih menolak selisih katalog basi. Penyimpangannya
                   terhadap master tetap dicatat server sebagai `sale_price_drift`. */
                ...(() => {
                    const up = input.lines.find((x) => x.code === l.code)?.price;
                    return up === undefined ? {} : { unitPrice: up };
                })(),
                /* ⭐ Modifier ikut dokumen (G-2). ⚠️ Tanpa harga — server menambahkannya dari master. */
                ...(() => {
                    const mods = input.lines.find((x) => x.code === l.code)?.modifiers;
                    return mods === undefined || mods.length === 0 ? {} : { modifiers: mods };
                })(),
                ...(() => {
                    const sel = input.lines.find((x) => x.code === l.code)?.comboSelections;
                    return sel === undefined || sel.length === 0 ? {} : { comboSelections: sel };
                })() })),
            tenders: [{
                    tenderId: uuidv7(),
                    paymentMethodCode: input.paymentMethodCode,
                    /* ⭐ Tender = persis yang ditagihkan. Kalau hitungan lokal keliru, server yang menolaknya. */
                    amount: grandTotal,
                }],
            catalogVersion: input.catalog.version,
            claimedGrandTotal: grandTotal,
            completedAt: new Date().toISOString(),
        },
        grandTotal,
        subtotalGross: moneyToWire(result.subtotalGross),
        serviceChargeTotal: moneyToWire(result.serviceChargeTotal),
        taxTotal: moneyToWire(result.taxTotal),
    };
}
/**
 * **Menyelesaikan dokumen penjualan tablet dengan tender yang SEBENARNYA.**
 *
 * ### ⛔⛔⛔ Kenapa fungsi ini ada
 * `assembleSale` dipanggil saat **bill dihitung** — jauh sebelum kasir memilih metode bayar. Ia
 * terpaksa memakai bawaan (`paymentMethods[0]`), dan sampai 2026-08-10 tak ada satu pun kode yang
 * memperbaikinya kemudian: cabang tablet di `pay()` hanya menandai layar lunas, dan objek metode
 * yang dipilih kasir tak pernah dibaca lagi.
 *
 * ⇒ **Penjualan QRIS tercatat sebagai TUNAI.** Bukan kosmetik: `tender_type` adalah klasifikasi
 * ekonomi (Guardrail #2), dan ERP memetakannya ke akun lewat `pos_tender_map`. Kas bertambah untuk
 * uang yang masih di penyelenggara QRIS — laci "kurang" tiap tutup shift, tanpa satu pun galat.
 *
 * ⚠️ Fungsi MURNI: mengembalikan dokumen baru, tak menyentuh yang lama. `orderId` sengaja TIDAK
 * berubah — ia kunci idempotensi seluruh dokumen (K13), dan mengganti tender bukan penjualan baru.
 */
export function settleSale(sale, tender) {
    const lama = sale.doc['tenders']?.[0];
    return {
        ...sale,
        doc: {
            ...sale.doc,
            tenders: [{
                    /* ⚠️ `tenderId` dipertahankan bila sudah ada: ia bagian dari identitas dokumen yang
                       mungkin sudah pernah dicoba kirim, dan mengubahnya membuat percobaan kedua terlihat
                       seperti tender kedua. */
                    tenderId: typeof lama?.tenderId === 'string' ? lama.tenderId : uuidv7(),
                    paymentMethodCode: tender.paymentMethodCode,
                    amount: tender.amount,
                    ...(tender.tenderedAmount === undefined ? {} : { tenderedAmount: tender.tenderedAmount }),
                    ...(tender.referenceNo === undefined ? {} : { referenceNo: tender.referenceNo }),
                    ...(tender.approvalCode === undefined ? {} : { approvalCode: tender.approvalCode }),
                    ...(tender.cardLast4 === undefined ? {} : { cardLast4: tender.cardLast4 }),
                }],
        },
    };
}
//# sourceMappingURL=assemble.js.map